import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TabAbonnement from '../tabs/TabAbonnement';
import { apiGet, apiPost } from '../utils/api-client';

/**
 * TabAbonnementContainer
 *
 * Endpoints branchés :
 *  - GET  /api/stripe/plan                       → { plan, plan_status,
 *                                                     cancel_at_period_end,
 *                                                     current_period_end }
 *  - POST /api/stripe/cancel-subscription        → résilie en fin de période
 *  - POST /api/stripe/resume-subscription        → annule la résiliation
 *  - POST /api/stripe/create-checkout-session    → { url } (Stripe Checkout)
 *  - POST /api/stripe/portal-session             → { url } (Stripe Billing Portal)
 *  - GET  /api/stripe/invoices                   → { invoices: [...] }
 *
 * Comportement :
 *  - Charge le plan actuel + factures au mount
 *  - Click sur un plan → POST checkout → redirect vers l'URL Stripe
 *  - Click "Gérer mon abonnement" → POST portal-session → redirect Stripe Portal
 *  - Click "Contacter le support" → mailto:
 */
/**
 * Motif d'arrivée sur /abonnement, lu une seule fois dans l'URL.
 * Sans lui, un utilisateur redirigé par un 402 (ou revenu d'un checkout annulé)
 * atterrit ici sans la moindre explication.
 *   ?reason=onboarding_payment_required → inscrit par la porte payante, checkout non finalisé
 *   ?reason=account_paused              → compte en pause / essai expiré
 *   ?paiement=cancel                    → a annulé le paiement chez Stripe
 *   ?checkout=failed                    → la session Stripe n'a pas pu être créée
 */
function readArrivalNotice() {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search);
  const reason = p.get('reason');
  if (reason === 'onboarding_payment_required') return 'onboarding_payment_required';
  if (reason === 'account_paused') return 'account_paused';
  if (p.get('paiement') === 'cancel') return 'payment_cancelled';
  if (p.get('checkout') === 'failed') return 'checkout_failed';
  return null;
}

export default function TabAbonnementContainer() {
  const { t } = useTranslation('tabs');
  const [arrivalNotice] = useState(readArrivalNotice);
  // null par défaut → empty state honnête côté composant tant qu'on ne sait pas.
  // Pas de fallback 'pro' / 'Actif' qui ferait croire à l'utilisateur qu'il a un plan
  // alors qu'il n'en a aucun (cas inscription récente, abonnement annulé, erreur API).
  const [currentPlan, setCurrentPlan]     = useState(null);
  const [planStatus, setPlanStatus]       = useState(null);
  const [planStatusRaw, setPlanStatusRaw] = useState(null);
  // Résiliation programmée : l'abonnement reste ACTIF jusqu'à currentPeriodEnd.
  // Sans ces deux états, la page affichait « Actif » à un abonné qui venait de
  // résilier — le bug qui a motivé ce chantier.
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd]   = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [invoices, setInvoices]           = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  /**
   * Charge l'état d'abonnement. Extrait du useEffect pour être rejoué après une
   * résiliation ou une reprise : les routes renvoient déjà le nouvel état, mais
   * on relit la source de vérité plutôt que de faire confiance à un état local.
   */
  const loadPlan = useCallback(async () => {
    const data = await apiGet('/api/stripe/plan');
    // Plan absent ou statut explicitement "canceled"/"unpaid" → on laisse null
    // pour déclencher l'empty state.
    if (data?.plan) setCurrentPlan(data.plan);
    if (data?.plan_status) {
      setPlanStatusRaw(data.plan_status);
      // Mappe les status backend → labels traduits
      const statusKey = `abonnement.status.${data.plan_status}`;
      setPlanStatus(t(statusKey, data.plan_status));
    }
    setCancelAtPeriodEnd(data?.cancel_at_period_end === true);
    setCurrentPeriodEnd(data?.current_period_end || null);
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadPlan();
        if (cancelled) return;
      } catch (e) {
        console.error('[TabAbonnement] load plan error:', e);
        // En cas d'erreur API : on RESTE en null (empty state) plutôt que de mentir
        // à l'utilisateur en affichant "Plan Pro — Actif" par défaut.
      }
    })();
    return () => { cancelled = true; };
  }, [loadPlan]);

  // Chargement des factures (séparé du plan pour ne pas bloquer l'UI principale)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet('/api/stripe/invoices');
        if (cancelled) return;
        setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
      } catch (e) {
        console.error('[TabAbonnement] load invoices error:', e);
        if (!cancelled) setInvoices([]);
      } finally {
        if (!cancelled) setInvoicesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /** Click sur un plan → crée une session Stripe Checkout et redirige. */
  const handleSubscribe = useCallback(async (planId) => {
    try {
      const data = await apiPost('/api/stripe/create-checkout-session', { plan: planId });
      if (data?.url) {
        // Redirect vers Stripe — l'utilisateur revient sur /dashboard?paiement=success après paiement
        window.location.href = data.url;
      } else {
        alert(t('abonnement.alerts.checkout_url_missing'));
      }
    } catch (e) {
      alert(t('abonnement.alerts.checkout_failed', { error: e.message }));
    }
  }, [t]);

  /** Click "Gérer mon abonnement" → ouvre le Stripe Billing Portal. */
  const handleOpenPortal = useCallback(async () => {
    if (portalLoading) return; // anti double-clic
    setPortalLoading(true);
    try {
      const data = await apiPost('/api/stripe/portal-session');
      if (data?.url) {
        window.location.href = data.url;
      } else {
        alert(t('abonnement.alerts.portal_url_missing'));
        setPortalLoading(false);
      }
    } catch (e) {
      // 404 = pas de stripe_customer_id (compte trial) → message dédié
      if (e?.status === 404) {
        alert(t('abonnement.alerts.portal_no_subscription'));
      } else {
        alert(t('abonnement.alerts.portal_failed', { error: e.message }));
      }
      setPortalLoading(false);
    }
    // Pas de finally setPortalLoading(false) volontaire en cas de succès :
    // window.location.href déclenche une navigation, on garde le loading actif
    // pour éviter un double-clic pendant la redirection.
  }, [portalLoading, t]);

  /**
   * Résilie l'abonnement depuis Revyo (fin de période, jamais immédiat).
   * La confirmation est portée par le composant (modale), pas par un window.confirm :
   * c'est une action irréversible sur un produit payant, elle mérite mieux qu'une
   * boîte système.
   */
  const handleCancelSubscription = useCallback(async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      await apiPost('/api/stripe/cancel-subscription');
      await loadPlan();
    } catch (e) {
      if (e?.status === 404) {
        alert(t('abonnement.alerts.cancel_no_subscription'));
      } else {
        alert(t('abonnement.alerts.cancel_failed', { error: e.message }));
      }
    } finally {
      setCancelLoading(false);
    }
  }, [cancelLoading, loadPlan, t]);

  /** Annule une résiliation programmée — l'abonnement reprend son cours. */
  const handleResumeSubscription = useCallback(async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      await apiPost('/api/stripe/resume-subscription');
      await loadPlan();
    } catch (e) {
      if (e?.status === 404) {
        alert(t('abonnement.alerts.resume_not_possible'));
      } else {
        alert(t('abonnement.alerts.resume_failed', { error: e.message }));
      }
    } finally {
      setCancelLoading(false);
    }
  }, [cancelLoading, loadPlan, t]);

  /** Ouvrir un mailto vers le support Revyo. */
  const handleContactSupport = useCallback(() => {
    window.location.href = 'mailto:florian@getrevyo.com?subject=Question%20abonnement%20Revyo';
  }, []);

  // La carte "Moyen de paiement et factures" (portail Stripe) n'a de sens que si
  // l'utilisateur a un abonnement Stripe (donc un plan_status backend, peu importe
  // sa valeur : active / trialing / past_due / canceled — tous correspondent à un
  // stripe_customer_id existant). Depuis l'ajout de la résiliation in-app, le
  // portail ne sert plus qu'à la carte bancaire et aux factures.
  const canOpenPortal = !!planStatusRaw;

  return (
    <TabAbonnement
      arrivalNotice={arrivalNotice}
      currentPlan={currentPlan}
      planStatus={planStatus}
      planStatusRaw={planStatusRaw}
      invoices={invoices}
      invoicesLoading={invoicesLoading}
      canOpenPortal={canOpenPortal}
      portalLoading={portalLoading}
      cancelAtPeriodEnd={cancelAtPeriodEnd}
      currentPeriodEnd={currentPeriodEnd}
      cancelLoading={cancelLoading}
      onSubscribe={handleSubscribe}
      onOpenPortal={handleOpenPortal}
      onCancelSubscription={handleCancelSubscription}
      onResumeSubscription={handleResumeSubscription}
      onContactSupport={handleContactSupport}
    />
  );
}
